import React, { useState, useEffect, useCallback } from 'react';
import API, { BACKEND_URL } from '../api';
import { Truck, Plus, Search, Trash2, Edit2, Mail, FileText, TrendingUp, Star, ArrowLeft, Upload, X, Send, BarChart3, Package, Euro, Clock, ExternalLink, Phone, AtSign } from 'lucide-react';
import RowActions from '../components/RowActions';
import { useConfirm } from '../components/ConfirmDialog';

const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function FornitoriPage() {
  const confirm = useConfirm();
  const [view, setView] = useState('list'); // list | detail | compare
  const [fornitori, setFornitori] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await API.get(`/api/fornitori${search ? `?q=${encodeURIComponent(search)}` : ''}`);
    setFornitori(r.data);
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const elimina = async (id) => {
    if (!await confirm({ title: 'Eliminare fornitore?', message: 'Verranno cancellati anche articoli, documenti ed email collegate.', confirmLabel: 'Elimina', variant: 'danger' })) return;
    await API.delete(`/api/fornitori/${id}`);
    load();
  };

  if (view === 'compare') return <ComparePage onBack={() => setView('list')} />;
  if (view === 'detail' && selected) return <FornitoreDetail id={selected} onBack={() => { setView('list'); setSelected(null); load(); }} />;

  return (
    <div className="animate-fade-in" data-testid="fornitori-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2"><Truck size={26} className="text-primary" /> Fornitori</h1>
          <p className="text-slate-500 text-sm mt-1">Anagrafica, documenti, storico prezzi e comparazione</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView('compare')} data-testid="compare-btn" className="px-3 py-2 bg-slate-900 text-white rounded-md text-sm font-bold flex items-center gap-1 hover:bg-slate-700"><BarChart3 size={14} /> Compara Prezzi</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-fornitore-btn" className="px-4 py-2 bg-primary text-white rounded-md text-sm font-bold flex items-center gap-1 hover:bg-primary/90"><Plus size={14} /> Nuovo</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 relative">
        <Search size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca ragione sociale, P.IVA, referente..." className="input w-full pl-9" data-testid="fornitori-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {fornitori.length === 0 && <p className="col-span-full text-center text-slate-400 py-8">Nessun fornitore. Aggiungi il primo.</p>}
        {fornitori.map(f => (
          <div key={f.id} onClick={() => { setSelected(f.id); setView('detail'); }} data-testid={`forn-${f.id}`}
            className="bg-white border border-slate-200 rounded-md p-4 cursor-pointer hover:border-primary hover:shadow-sm transition-all">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <p className="font-black truncate">{f.ragione_sociale}</p>
                {f.referente && <p className="text-xs text-slate-500 truncate">{f.referente}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {[1,2,3,4,5].map(n => <Star key={n} size={10} className={n <= (f.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'} />)}
              </div>
            </div>
            <div className="space-y-1 text-xs text-slate-600">
              {f.email && <p className="flex items-center gap-1 truncate"><AtSign size={10} /> {f.email}</p>}
              {f.telefono && <p className="flex items-center gap-1"><Phone size={10} /> {f.telefono}</p>}
              {f.citta && <p className="text-slate-400">{f.citta} {f.provincia && `(${f.provincia})`}</p>}
            </div>
            {f.categorie && f.categorie.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {f.categorie.slice(0, 3).map((c, i) => <span key={`${c}-${i}`} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-bold">{c}</span>)}
              </div>
            )}
            <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => { setEditing(f); setShowForm(true); }} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"><Edit2 size={12} /></button>
              <RowActions entity="fornitori" id={f.id} label="il fornitore" onChange={load} compact />
            </div>
          </div>
        ))}
      </div>

      {showForm && <FornitoreForm editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function FornitoreForm({ editing, onClose, onSaved }) {
  const [d, setD] = useState(editing || { ragione_sociale: '', piva: '', email: '', telefono: '', referente: '', referente_email: '', citta: '', provincia: '', indirizzo: '', cap: '', pec: '', iban: '', condizioni_pagamento: '30gg f.m.', rating: 0, note: '', categorie: [], sito_web: '' });
  const [cat, setCat] = useState('');
  const save = async () => {
    try {
      if (editing) await API.put(`/api/fornitori/${editing.id}`, d);
      else await API.post('/api/fornitori', d);
      onSaved();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-5xl w-full max-h-[90vh] overflow-auto shadow-2xl" data-testid="forn-form">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">{editing ? 'Modifica' : 'Nuovo'} Fornitore</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input data-testid="forn-ragsoc" placeholder="Ragione Sociale *" className="input w-full" value={d.ragione_sociale} onChange={(e) => setD({ ...d, ragione_sociale: e.target.value })} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input placeholder="P.IVA" className="input" value={d.piva} onChange={(e) => setD({ ...d, piva: e.target.value })} />
            <input placeholder="C.F." className="input" value={d.cf || ''} onChange={(e) => setD({ ...d, cf: e.target.value })} />
            <input placeholder="Sito web" className="input" value={d.sito_web || ''} onChange={(e) => setD({ ...d, sito_web: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="Email" className="input" value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} data-testid="forn-email" />
            <input placeholder="PEC" className="input" value={d.pec || ''} onChange={(e) => setD({ ...d, pec: e.target.value })} />
            <input placeholder="Telefono" className="input" value={d.telefono} onChange={(e) => setD({ ...d, telefono: e.target.value })} />
            <input placeholder="IBAN" className="input" value={d.iban || ''} onChange={(e) => setD({ ...d, iban: e.target.value })} />
          </div>
          <fieldset className="border rounded p-3">
            <legend className="text-xs font-bold text-slate-500">Referente</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input placeholder="Nome" className="input" value={d.referente} onChange={(e) => setD({ ...d, referente: e.target.value })} />
              <input placeholder="Email referente" className="input" value={d.referente_email} onChange={(e) => setD({ ...d, referente_email: e.target.value })} />
              <input placeholder="Telefono" className="input" value={d.referente_telefono || ''} onChange={(e) => setD({ ...d, referente_telefono: e.target.value })} />
            </div>
          </fieldset>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input placeholder="Indirizzo" className="input sm:col-span-2" value={d.indirizzo} onChange={(e) => setD({ ...d, indirizzo: e.target.value })} />
            <input placeholder="Citta" className="input" value={d.citta} onChange={(e) => setD({ ...d, citta: e.target.value })} />
            <div className="flex gap-2">
              <input placeholder="CAP" className="input w-full" value={d.cap} onChange={(e) => setD({ ...d, cap: e.target.value })} />
              <input placeholder="Prov" className="input w-20" maxLength="2" value={d.provincia} onChange={(e) => setD({ ...d, provincia: e.target.value.toUpperCase() })} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="Condizioni pagamento" className="input" value={d.condizioni_pagamento} onChange={(e) => setD({ ...d, condizioni_pagamento: e.target.value })} />
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">Rating:</span>
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setD({ ...d, rating: n })} type="button">
                  <Star size={16} className={n <= (d.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Categorie (es. cemento, ferro, pavimenti)</label>
            <div className="flex flex-wrap gap-1 mb-2 mt-1">
              {(d.categorie || []).map((c, i) => (
                <span key={i} className="px-2 py-0.5 bg-primary text-primary rounded text-xs flex items-center gap-1">{c} <button onClick={() => setD({ ...d, categorie: d.categorie.filter((_, x) => x !== i) })}><X size={10} /></button></span>
              ))}
            </div>
            <div className="flex gap-2">
              <input placeholder="Nuova categoria..." className="input flex-1" value={cat} onChange={(e) => setCat(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && cat.trim()) { setD({ ...d, categorie: [...(d.categorie || []), cat.trim()] }); setCat(''); } }} />
              <button onClick={() => { if (cat.trim()) { setD({ ...d, categorie: [...(d.categorie || []), cat.trim()] }); setCat(''); } }} className="px-3 bg-slate-100 rounded"><Plus size={14} /></button>
            </div>
          </div>
          <textarea placeholder="Note" rows="2" className="input w-full" value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} />
        </div>
        <div className="sticky bottom-0 bg-white border-t p-3 text-right">
          <button onClick={save} data-testid="save-forn" className="bg-primary text-white px-4 py-2 rounded-md text-sm font-bold">Salva</button>
        </div>
      </div>
    </div>
  );
}

function FornitoreDetail({ id, onBack }) {
  const [tab, setTab] = useState('info');
  const [f, setF] = useState(null);
  const load = useCallback(() => API.get(`/api/fornitori/${id}`).then(r => setF(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!f) return <p className="text-slate-400 py-8">Caricamento...</p>;

  const tabs = [
    { id: 'info', l: 'Info', icon: Truck },
    { id: 'articoli', l: 'Articoli / Prezzi', icon: Package },
    { id: 'documenti', l: 'Documenti / Bolle', icon: FileText },
    { id: 'email', l: 'Comunicazioni', icon: Mail },
  ];

  return (
    <div className="animate-fade-in" data-testid="forn-detail">
      <button onClick={onBack} className="mb-4 flex items-center gap-1 text-slate-600 hover:text-primary text-sm font-bold"><ArrowLeft size={14} /> Torna all'elenco</button>
      <div className="bg-white border rounded-md p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-black">{f.ragione_sociale}</h1>
            <p className="text-sm text-slate-500">{f.referente} · {f.email} · {f.telefono}</p>
            <div className="flex gap-1 mt-1">{[1,2,3,4,5].map(n => <Star key={n} size={12} className={n <= (f.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'} />)}</div>
          </div>
          <div className="text-right text-sm">
            <p className="text-slate-500">Totale ordinato</p>
            <p className="text-xl font-black font-mono">{fmt(f.stats?.totale_ordinato)}</p>
            <p className="text-xs text-slate-400">{f.stats?.num_articoli} articoli · {f.stats?.num_documenti} docs · {f.stats?.num_emails} email</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b mb-4 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-slate-500'}`}>
            <t.icon size={14} /> {t.l}
          </button>
        ))}
      </div>

      {tab === 'info' && <InfoTab f={f} />}
      {tab === 'articoli' && <ArticoliTab fid={id} />}
      {tab === 'documenti' && <DocumentiTab fid={id} />}
      {tab === 'email' && <EmailTab fid={id} forn={f} />}
    </div>
  );
}

function InfoTab({ f }) {
  const rows = [
    ['Ragione Sociale', f.ragione_sociale], ['P.IVA', f.piva || '-'], ['C.F.', f.cf || '-'],
    ['Indirizzo', `${f.indirizzo || ''} ${f.citta || ''} ${f.cap || ''} ${f.provincia ? `(${f.provincia})` : ''}`.trim() || '-'],
    ['Email', f.email || '-'], ['PEC', f.pec || '-'], ['Telefono', f.telefono || '-'], ['Sito web', f.sito_web || '-'],
    ['Referente', f.referente || '-'], ['Email referente', f.referente_email || '-'], ['Tel referente', f.referente_telefono || '-'],
    ['IBAN', f.iban || '-'], ['Condizioni pagamento', f.condizioni_pagamento || '-'], ['Categorie', (f.categorie || []).join(', ') || '-'],
    ['Note', f.note || '-'],
  ];
  return (
    <div className="bg-white border rounded-md p-4">
      <dl className="space-y-2">{rows.map(([k, v], i) => <div key={i} className="flex items-start gap-3 border-b border-slate-50 pb-2"><dt className="text-xs font-bold text-slate-500 uppercase w-36 flex-shrink-0">{k}</dt><dd className="text-sm">{v}</dd></div>)}</dl>
    </div>
  );
}

function ArticoliTab({ fid }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(() => API.get(`/api/fornitori/${fid}/articoli`).then(r => setItems(r.data)), [fid]);
  useEffect(() => { load(); }, [load]);

  const save = async (d) => {
    try {
      if (editing) await API.put(`/api/fornitori/${fid}/articoli/${editing.id}`, d);
      else await API.post(`/api/fornitori/${fid}/articoli`, d);
      setShowForm(false); setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (await confirm({ title: 'Eliminare articolo?', message: 'L\'articolo verrà rimosso dal listino fornitore.', confirmLabel: 'Elimina', variant: 'danger' })) { await API.delete(`/api/fornitori/${fid}/articoli/${id}`); load(); } };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-primary text-white px-3 py-1.5 rounded text-sm font-bold flex items-center gap-1" data-testid="add-articolo"><Plus size={12} /> Articolo</button>
      </div>
      <div className="bg-white border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className="text-left px-3 py-2">Nome</th><th className="text-left px-3 py-2">Codice</th><th className="text-left px-3 py-2">U.M.</th><th className="text-right px-3 py-2">Prezzo</th><th className="text-right px-3 py-2">Sconto %</th><th className="text-center px-3 py-2">Storico</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="text-center text-slate-400 py-6">Nessun articolo</td></tr>}
            {items.map(a => (
              <React.Fragment key={a.id}>
                <tr className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold">{a.nome}</td>
                  <td className="px-3 py-2 text-slate-500">{a.codice_articolo || '-'}</td>
                  <td className="px-3 py-2">{a.unita_misura}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{fmt(a.prezzo_corrente)}</td>
                  <td className="px-3 py-2 text-right">{a.sconto_pct || 0}%</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} className="text-xs text-primary font-bold hover:underline">{(a.storico_prezzi || []).length} variazioni</button>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(a); setShowForm(true); }} className="p-1 text-blue-500"><Edit2 size={12} /></button>
                    <button onClick={() => del(a.id)} className="p-1 text-red-500"><Trash2 size={12} /></button>
                  </td>
                </tr>
                {expanded === a.id && (
                  <tr className="bg-slate-50"><td colSpan="7" className="px-6 py-3">
                    <p className="text-xs font-bold uppercase text-slate-500 mb-2">Storico prezzi</p>
                    <ul className="space-y-1">{(a.storico_prezzi || []).slice().reverse().map((s, i) => <li key={`${s.data || ''}-${s.prezzo}-${i}`} className="text-xs flex items-center gap-3"><Clock size={10} /> <span>{fmtDate(s.data)}</span> <span className="font-mono font-bold">{fmt(s.prezzo)}</span> {s.variazione_pct !== undefined && <span className={s.variazione_pct > 0 ? 'text-red-600' : 'text-green-600'}>({s.variazione_pct > 0 ? '+' : ''}{s.variazione_pct}%)</span>} {s.note && <span className="text-slate-400">· {s.note}</span>}</li>)}</ul>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <ArticoloForm editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} />}
    </div>
  );
}

function ArticoloForm({ editing, onClose, onSave }) {
  const [d, setD] = useState(editing || { nome: '', codice_articolo: '', categoria: '', unita_misura: 'cad', prezzo_corrente: 0, sconto_pct: 0, tempo_consegna_gg: 0, descrizione: '' });
  const [nota, setNota] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-4xl w-full p-6 shadow-2xl">
        <h2 className="text-lg font-bold mb-3">{editing ? 'Modifica Articolo' : 'Nuovo Articolo'}</h2>
        <div className="space-y-2">
          <input placeholder="Nome *" className="input w-full" value={d.nome} onChange={(e) => setD({ ...d, nome: e.target.value })} data-testid="art-nome" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Codice" className="input" value={d.codice_articolo} onChange={(e) => setD({ ...d, codice_articolo: e.target.value })} />
            <input placeholder="Categoria (es. cemento)" className="input" value={d.categoria} onChange={(e) => setD({ ...d, categoria: e.target.value })} />
          </div>
          <textarea placeholder="Descrizione" rows="2" className="input w-full" value={d.descrizione || ''} onChange={(e) => setD({ ...d, descrizione: e.target.value })} />
          <div className="grid grid-cols-4 gap-2">
            <input placeholder="U.M." className="input" value={d.unita_misura} onChange={(e) => setD({ ...d, unita_misura: e.target.value })} />
            <input type="number" step="0.01" placeholder="Prezzo" className="input" value={d.prezzo_corrente} onChange={(e) => setD({ ...d, prezzo_corrente: e.target.value })} data-testid="art-prezzo" />
            <input type="number" placeholder="Sconto %" className="input" value={d.sconto_pct} onChange={(e) => setD({ ...d, sconto_pct: e.target.value })} />
            <input type="number" placeholder="gg consegna" className="input" value={d.tempo_consegna_gg} onChange={(e) => setD({ ...d, tempo_consegna_gg: e.target.value })} />
          </div>
          {editing && <input placeholder="Nota per variazione prezzo" className="input w-full" value={nota} onChange={(e) => setNota(e.target.value)} />}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm">Annulla</button>
          <button onClick={() => onSave({ ...d, nota_prezzo: nota })} className="bg-primary text-white px-4 py-2 rounded text-sm font-bold" data-testid="save-articolo">Salva</button>
        </div>
      </div>
    </div>
  );
}

function DocumentiTab({ fid }) {
  const confirm = useConfirm();
  const [docs, setDocs] = useState([]);
  const [upload, setUpload] = useState({ tipo: 'bolla', numero: '', data: new Date().toISOString().slice(0,10), note: '' });
  const fileRef = React.useRef(null);
  const load = useCallback(() => API.get(`/api/fornitori/${fid}/documenti`).then(r => setDocs(r.data)), [fid]);
  useEffect(() => { load(); }, [load]);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('tipo', upload.tipo); fd.append('numero_documento', upload.numero); fd.append('data_documento', upload.data); fd.append('note', upload.note);
    try { await API.post(`/api/fornitori/${fid}/documenti`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); load(); e.target.value = ''; setUpload({ ...upload, numero: '', note: '' }); }
    catch (er) { alert(er.response?.data?.detail || er.message); }
  };
  const del = async (did) => { if (await confirm({ title: 'Eliminare documento?', message: 'Il file verrà rimosso dai documenti fornitore.', confirmLabel: 'Elimina', variant: 'danger' })) { await API.delete(`/api/fornitori/${fid}/documenti/${did}`); load(); } };
  const apiBase = BACKEND_URL;
  return (
    <div>
      <div className="bg-white border rounded-md p-3 mb-3 grid grid-cols-1 sm:grid-cols-5 gap-2">
        <select className="input" value={upload.tipo} onChange={(e) => setUpload({...upload, tipo: e.target.value})}>
          <option value="bolla">Bolla</option><option value="fattura">Fattura</option><option value="documento">Documento</option><option value="contratto">Contratto</option><option value="certificato">Certificato</option>
        </select>
        <input placeholder="N. documento" className="input" value={upload.numero} onChange={(e) => setUpload({...upload, numero: e.target.value})} />
        <input type="date" className="input" value={upload.data} onChange={(e) => setUpload({...upload, data: e.target.value})} />
        <input placeholder="Note" className="input" value={upload.note} onChange={(e) => setUpload({...upload, note: e.target.value})} />
        <input ref={fileRef} type="file" onChange={handleUpload} className="hidden" data-testid="doc-file" />
        <button onClick={() => fileRef.current?.click()} className="bg-primary text-white rounded text-sm font-bold flex items-center justify-center gap-1"><Upload size={12} /> Carica</button>
      </div>
      <div className="bg-white border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className="text-left px-3 py-2">Tipo</th><th className="text-left px-3 py-2">Numero</th><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">File</th><th className="text-left px-3 py-2">Note</th><th></th></tr></thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan="6" className="text-center text-slate-400 py-6">Nessun documento</td></tr>}
            {docs.map(doc => (
              <tr key={doc.id} className="border-t">
                <td className="px-3 py-2"><span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-bold">{doc.tipo}</span></td>
                <td className="px-3 py-2 font-mono">{doc.numero_documento || '-'}</td>
                <td className="px-3 py-2 text-slate-600">{fmtDate(doc.data_documento)}</td>
                <td className="px-3 py-2"><a href={apiBase + doc.url} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1 text-xs"><ExternalLink size={10} /> {doc.nome_file}</a></td>
                <td className="px-3 py-2 text-xs text-slate-500">{doc.note}</td>
                <td className="px-3 py-2 text-right"><button onClick={() => del(doc.id)} className="text-red-500 p-1"><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailTab({ fid, forn }) {
  const [templates, setTemplates] = useState([]);
  const [emails, setEmails] = useState([]);
  const [articoli, setArticoli] = useState([]);
  const [selTpl, setSelTpl] = useState('richiesta_quotazione');
  const [selArts, setSelArts] = useState([]);
  const [customOgg, setCustomOgg] = useState('');
  const [customCorpo, setCustomCorpo] = useState('');
  const [preview, setPreview] = useState(null);

  const loadEmails = useCallback(() => API.get(`/api/fornitori/${fid}/emails`).then(r => setEmails(r.data)), [fid]);

  useEffect(() => {
    API.get('/api/email-templates').then(r => setTemplates(r.data));
    API.get(`/api/fornitori/${fid}/articoli`).then(r => setArticoli(r.data));
    loadEmails();
  }, [fid, loadEmails]);

  const prepara = async () => {
    try {
      const r = await API.post(`/api/fornitori/${fid}/email/prepare`, { template_id: selTpl, articoli_ids: selArts, oggetto: customOgg, corpo: customCorpo });
      setPreview(r.data);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const apriEmail = () => {
    if (!preview) return;
    window.location.href = preview.mailto;
    API.put(`/api/fornitori/${fid}/emails/${preview.email_id}/sent`).then(() => loadEmails());
    setTimeout(() => setPreview(null), 1000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white border rounded-md p-4" data-testid="email-compose">
        <h3 className="font-bold mb-3 flex items-center gap-1"><Send size={14} /> Componi Email</h3>
        <p className="text-xs text-slate-500 mb-3">Destinatario: <span className="font-bold">{forn.referente_email || forn.email || forn.pec || '(nessuno - configura email)'}</span></p>
        <label className="text-xs font-bold text-slate-500">Template</label>
        <select value={selTpl} onChange={(e) => setSelTpl(e.target.value)} className="input w-full mb-2" data-testid="email-template">
          {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        {articoli.length > 0 && (
          <>
            <label className="text-xs font-bold text-slate-500">Articoli da includere (opzionale)</label>
            <div className="max-h-32 overflow-y-auto border rounded p-2 mb-2">
              {articoli.map(a => (
                <label key={a.id} className="flex items-center gap-2 text-xs py-0.5">
                  <input type="checkbox" checked={selArts.includes(a.id)} onChange={(e) => setSelArts(e.target.checked ? [...selArts, a.id] : selArts.filter(x => x !== a.id))} />
                  <span>{a.nome} ({fmt(a.prezzo_corrente)}/{a.unita_misura})</span>
                </label>
              ))}
            </div>
          </>
        )}
        <details className="mb-3">
          <summary className="text-xs text-primary font-bold cursor-pointer">Personalizza oggetto e corpo (override template)</summary>
          <input placeholder="Oggetto custom" className="input w-full mt-2" value={customOgg} onChange={(e) => setCustomOgg(e.target.value)} />
          <textarea placeholder="Corpo custom" rows="4" className="input w-full mt-1" value={customCorpo} onChange={(e) => setCustomCorpo(e.target.value)} />
        </details>
        <button onClick={prepara} className="bg-primary text-white w-full py-2 rounded font-bold" data-testid="prepare-email">Anteprima Email</button>

        {preview && (
          <div className="mt-3 bg-slate-50 border rounded p-3" data-testid="email-preview">
            <p className="text-xs"><strong>A:</strong> {preview.destinatario}</p>
            <p className="text-xs"><strong>Oggetto:</strong> {preview.oggetto}</p>
            <pre className="text-xs whitespace-pre-wrap mt-2 bg-white p-2 rounded border max-h-40 overflow-auto">{preview.corpo}</pre>
            <div className="flex gap-2 mt-2">
              <button onClick={apriEmail} className="flex-1 bg-green-600 text-white py-1.5 rounded text-sm font-bold flex items-center justify-center gap-1" data-testid="send-email"><ExternalLink size={12} /> Apri nel client email</button>
              <button onClick={() => setPreview(null)} className="px-3 bg-slate-200 rounded text-sm">Annulla</button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Si aprira il tuo Outlook/Gmail/ecc con tutto pronto. Premi Invia in quella finestra.</p>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-md p-4">
        <h3 className="font-bold mb-3 flex items-center gap-1"><Mail size={14} /> Storico Comunicazioni</h3>
        {emails.length === 0 ? <p className="text-slate-400 text-sm py-3">Nessuna email</p> : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {emails.map(e => (
              <div key={e.id} className="border rounded p-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${e.stato === 'inviata' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{e.stato}</span>
                  <span className="text-slate-400">{fmtDate(e.created_at)}</span>
                </div>
                <p className="font-semibold">{e.oggetto}</p>
                <p className="text-slate-500 truncate">{e.corpo.slice(0, 120)}...</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ComparePage({ onBack }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const r = await API.get(`/api/fornitori-comparazione${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    setData(r.data);
  }, [q]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="animate-fade-in" data-testid="compare-page">
      <button onClick={onBack} className="mb-3 flex items-center gap-1 text-sm font-bold"><ArrowLeft size={14} /> Indietro</button>
      <h1 className="text-2xl font-black mb-3 flex items-center gap-2"><BarChart3 size={22} className="text-primary" /> Comparazione Prezzi</h1>
      <input placeholder="Cerca articolo o categoria..." className="input w-full mb-4" value={q} onChange={(e) => setQ(e.target.value)} />
      {!data ? <p className="text-slate-400">Caricamento...</p> : (
        <div className="space-y-3">
          {data.articoli.length === 0 && <p className="text-center text-slate-400 py-8">Nessun articolo comparabile. Aggiungi lo stesso articolo a piu fornitori.</p>}
          {data.articoli.map((a, i) => (
            <div key={a.nome || i} className="bg-white border rounded-md p-4" data-testid={`compare-row-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-black">{a.nome}</h3>
                  <p className="text-xs text-slate-500">{a.categoria} · {a.num_fornitori} fornitori a confronto</p>
                </div>
                {a.risparmio_pct > 0 && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">Risparmio fino a {a.risparmio_pct}%</span>}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500"><tr><th className="text-left pb-1">Fornitore</th><th className="text-right pb-1">Lista</th><th className="text-right pb-1">Sconto</th><th className="text-right pb-1">Netto</th><th className="text-center pb-1">Consegna</th></tr></thead>
                <tbody>
                  {a.offerte.map((o, j) => (
                    <tr key={j} className={j === 0 ? 'bg-green-50' : ''}>
                      <td className="py-1.5 font-semibold">{j === 0 && '🏆 '}{o.fornitore_nome}</td>
                      <td className="text-right font-mono">{fmt(o.prezzo)}</td>
                      <td className="text-right">{o.sconto_pct}%</td>
                      <td className={`text-right font-mono font-bold ${j === 0 ? 'text-green-600' : ''}`}>{fmt(o.prezzo_netto)}</td>
                      <td className="text-center text-xs">{o.tempo_consegna_gg}gg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
